import React, { useEffect, useState } from "react";
import CreateButton from "../CreateButton/CreateButton";
import CancelButton from "../CancelButton/CancelButton";
import {
  createRestaurant,
  createRestaurantUser,
} from "../../redux/slices/allRestaurantsSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { DocumentUpload } from "iconsax-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

type ModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

const AddRestaurantModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const isModal = typeof isOpen === "boolean" && typeof onClose === "function";

  const [name, setName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [openingHours, setOpeningHours] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  const [errors, setErrors] = useState({
    name: "",
    emailAddress: "",
    phoneNumber: "",
    ownerName: "",
    ownerEmail: "",
    openingHours: "",
    location: "",
    category: "",
    image: "",
  });

  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector((state: any) => state.restaurants);
  const { error: userError } = useSelector((state: any) => state.allUsers);

  useEffect(() => {
    if (!isModal || !isOpen || !onClose) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isModal, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let formIsValid = true;
    const newErrors: any = {};

    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

    if (!name) {
      newErrors.name = t("addRestaurantModal.validation.nameRequired");
      formIsValid = false;
    }

    if (!emailAddress) {
      newErrors.emailAddress = t("addRestaurantModal.validation.emailRequired");
      formIsValid = false;
    } else if (!emailPattern.test(emailAddress)) {
      newErrors.emailAddress = t("addRestaurantModal.validation.emailInvalid");
      formIsValid = false;
    }

    if (!phoneNumber) {
      newErrors.phoneNumber = t(
        "addRestaurantModal.validation.phoneNumberRequired"
      );
      formIsValid = false;
    }

    if (!ownerName) {
      newErrors.ownerName = t(
        "addRestaurantModal.validation.ownerNameRequired"
      );
      formIsValid = false;
    }

    if (!ownerEmail) {
      newErrors.ownerEmail = t(
        "addRestaurantModal.validation.ownerEmailRequired"
      );
      formIsValid = false;
    } else if (!emailPattern.test(ownerEmail)) {
      newErrors.ownerEmail = t("addRestaurantModal.validation.emailInvalid");
      formIsValid = false;
    }

    if (!openingHours) {
      newErrors.openingHours = t(
        "addRestaurantModal.validation.openingHoursRequired"
      );
      formIsValid = false;
    } else {
      const openingHoursPattern =
        /^([01]?[0-9]|2[0-3]):([0-5][0-9])-([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!openingHoursPattern.test(openingHours)) {
        newErrors.openingHours = t(
          "addRestaurantModal.validation.openingHoursInvalid"
        );
        formIsValid = false;
      }
    }

    if (!location) {
      newErrors.location = t("addRestaurantModal.validation.locationRequired");
      formIsValid = false;
    }

    if (!image) {
      newErrors.image = t("addRestaurantModal.validation.imageRequired");
      formIsValid = false;
    }

    if (!category) {
      newErrors.category = t("addRestaurantModal.validation.categoryRequired");
      formIsValid = false;
    }

    setErrors(newErrors);

    if (formIsValid) {
      const payload = {
        name,
        emailAddress,
        phoneNumber,
        adminName: ownerName,
        ownerName,
        ownerEmail,
        openingHours,
        location,
        category,
        image,
      };

      if (isModal) {
        dispatch(createRestaurant(payload));
      } else {
        dispatch(createRestaurantUser(payload));
      }

      // Clear form
      setName("");
      setEmailAddress("");
      setPhoneNumber("");
      setOwnerName("");
      setOwnerEmail("");
      setOpeningHours("");
      setLocation("");
      setCategory("");
      setImage(null);
      setImagePreview(null);
      setShowImagePreview(false);

      if (isModal) onClose?.();
    }
  };

  const FormContent = (
    <div className="">
      <h2 className="mb-4 text-xl font-medium">
        {t("addRestaurantModal.title")}
      </h2>
      <h2 className="mb-2 font-medium">
        {t("addRestaurantModal.createProfileTitle")}
      </h2>
      <form className="w-full mt-4" onSubmit={handleSubmit}>
        <div className="flex gap-4 mb-6">
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.restaurantNameLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.restaurantNamePlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prevErrors) => ({ ...prevErrors, name: "" })); // Clear error when user types
              }}
            />
            {errors.name && (
              <p className="text-xs text-red-600">{errors.name}</p>
            )}
          </div>
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.emailAddressLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.emailAddressPlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={emailAddress}
              onChange={(e) => {
                setEmailAddress(e.target.value);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  emailAddress: "",
                })); // Clear error when user types
              }}
            />
            {errors.emailAddress && (
              <p className="text-xs text-red-600">{errors.emailAddress}</p>
            )}
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.restaurantLogoLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <label className="border h-[40px] rounded-md pl-4 text-sm focus:border-2 focus:outline-none cursor-pointer flex justify-between items-center bg-white relative">
              <input
                type="file"
                name="file_upload"
                className="hidden"
                onChange={(e) => {
                  const selectedFile = e.target.files && e.target.files[0];
                  setImage(selectedFile || null);
                  setErrors((prevErrors) => ({ ...prevErrors, image: "" }));
                }}
              />
              <span
                className={`flex items-center h-full text-sm ${
                  image ? "text-black" : "text-gray-400"
                }`}
              >
                {image ? (
                  <div className="flex items-center gap-2">
                    {image.name}
                    <button
                      type="button"
                      onClick={() => setImage(null)}
                      className="text-gray-400 hover:text-red-600 text-xs font-bold"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  t("addRestaurantModal.uploadImagePlaceholder")
                )}
              </span>
              <div className="p-1 mr-3 bg-gray-100 rounded-full">
                <DocumentUpload size="18" color="#D10000" variant="Outline" />
              </div>
            </label>

            {errors.image && (
              <p className="text-xs text-red-600">{errors.image}</p>
            )}
          </div>
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.openingHoursLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.openingHoursPlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={openingHours}
              onChange={(e) => {
                setOpeningHours(e.target.value);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  openingHours: "",
                })); // Clear error when user types
              }}
            />
            {errors.openingHours && (
              <p className="text-xs text-red-600">{errors.openingHours}</p>
            )}
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.ownerNameLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.ownerNamePlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={ownerName}
              onChange={(e) => {
                setOwnerName(e.target.value);
                setErrors((prevErrors) => ({ ...prevErrors, ownerName: "" })); // Clear error when user types
              }}
            />
            {errors.ownerName && (
              <p className="text-xs text-red-600">{errors.ownerName}</p>
            )}
          </div>
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.ownerEmailLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.ownerEmailPlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={ownerEmail}
              onChange={(e) => {
                setOwnerEmail(e.target.value);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  ownerEmail: "",
                })); // Clear error when user types
              }}
            />
            {errors.ownerEmail && (
              <p className="text-xs text-red-600">{errors.ownerEmail}</p>
            )}
          </div>
        </div>

        {isModal && (
          <h2 className="mt-8 mb-2 font-medium">
            {t("addRestaurantModal.restaurantDetailsTitle")}
          </h2>
        )}

        <div className="flex gap-4 mt-4 mb-6">
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.locationLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.locationPlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setErrors((prevErrors) => ({ ...prevErrors, location: "" })); // Clear error when user types
              }}
            />
            {errors.location && (
              <p className="text-xs text-red-600">{errors.location}</p>
            )}
          </div>
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.categoryLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              placeholder={t("addRestaurantModal.categoryPlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setErrors((prevErrors) => ({ ...prevErrors, category: "" })); // Clear error when user types
              }}
            />
            {errors.category && (
              <p className="text-xs text-red-600">{errors.category}</p>
            )}
          </div>
          <div className="flex flex-col w-6/12 gap-1">
            <label className="text-xs font-medium">
              {t("addRestaurantModal.phoneNumberLabel")}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              type="number"
              placeholder={t("addRestaurantModal.phoneNumberPlaceholder")}
              className="border h-[40px] rounded-md pl-3 text-sm focus:border-2 focus:outline-none leading-[40px]"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  phoneNumber: "",
                })); // Clear error when user types
              }}
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-600">{errors.phoneNumber}</p>
            )}
          </div>
        </div>

        {isModal ? (
          <div className="flex justify-end gap-2 mt-8">
            <CreateButton disabled={loading} />
            <CancelButton onClick={onClose} />
          </div>
        ) : (
          <>
            <div className="flex justify-center mt-8">
              <div className="mb-6 text-center">
                <div className="flex flex-col items-center justify-center gap-4">
                  <button
                    type="submit"
                    className="w-[273px] h-[40px] bg-loginButton rounded-lg text-white mt-4 text-sm"
                  >
                    {t("addRestaurantModal.signUpButton")}
                  </button>
                  <p className="text-sm text-black m-0">
                    {t("addRestaurantModal.alreadyHaveAccount")}{" "}
                    <Link
                      to="/login"
                      className="text-[#D10000] font-semibold hover:underline"
                    >
                      {t("addRestaurantModal.signInLink")}
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
  if (isModal && !isOpen) return null;

  return isModal ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white px-12 py-5 w-[1000px] relative">
        {FormContent}
      </div>
    </div>
  ) : (
    <div className="bg-bg-login/register px-12 w-[1000px] relative">
      {FormContent}
    </div>
  );
};

export default AddRestaurantModal;
